import { Component, Inject, ViewEncapsulation } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { finalize } from 'rxjs/operators';
import { AlcmeonContextService } from 'src/app/core/alcmeon-context/alcmeon-context.service';
import { SitesService } from 'src/app/mobile/shared/sites.service';
import { Choice } from 'src/app/shared/components/select/choice.model';
import { BasicService } from 'src/app/shared/services/basic.service';
import { LinkShortenerService } from './link-shortener.service';

@Component({
  selector: 'app-link-shortener',
  templateUrl: './link-shortener.component.html',
  styleUrls: ['./link-shortener.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class LinkShortenerComponent {
  siteControl: FormControl = new FormControl('', Validators.required);
  sitesChoices: Choice[] = this.dropdownItemsService.buildSiteItems(this.sitesService.sitesV2);

  localStorageKey = `links-site-id-for-company-${this.alcmeonContextService.companyId}`;

  form: FormGroup = this.formBuilder.group({
    allowed: [true],
    encryption_key: ['', Validators.required],
    authentication_key: ['', Validators.required],
  });

  subconfigs: any = [];
  linksSubmitDisabled = true;

  constructor(
    private formBuilder: FormBuilder,
    private linkShortenerService: LinkShortenerService,
    private alcmeonContextService: AlcmeonContextService,
    private sitesService: SitesService,
    private basicService: BasicService,
    @Inject('DropdownItemsFactory') private dropdownItemsService: any,
    @Inject('AlertingFactory') private alertingService: any,
    @Inject('TopToolbarFactory') topToolbarService: any
  ) {
    topToolbarService.setTitleTranslation('account/links', null);
    this.shouldDisableSaveForLinks(true);
    this.siteControl.valueChanges.subscribe(() => this.getLinks());
    setTimeout(() => this.siteControl.setValue(this.sitesService.sitesV2[0].id), 300);

    this.getSkeepersConnector();

    this.form.get('allowed').valueChanges.subscribe(value => {
      if (value) {
        this.form.get('encryption_key').enable();
        this.form.get('authentication_key').enable();
      } else {
        this.form.get('encryption_key').disable();
        this.form.get('authentication_key').disable();
      }
    });
  }

  shouldDisableSaveForLinks(state: boolean) {
    this.linksSubmitDisabled = state;
  }

  saveLinksChanges() {
    this.basicService.showSpinner();
    this.linkShortenerService
      .updateLinks(this.siteControl.value, this.subconfigs)
      .pipe(finalize(() => this.basicService.hideSpinner()))
      .subscribe({
        next: () => {
          this.alertingService.success('account/links/updated/ok');
          this.shouldDisableSaveForLinks(true);
        },
      });
  }

  saveConnectorChanges() {
    this.basicService.showSpinner();
    this.linkShortenerService
      .updateConnector(this.form.value)
      .pipe(finalize(() => this.basicService.hideSpinner()))
      .subscribe({
        next: () => this.alertingService.success('account/links/updated/ok'),
      });
  }

  cancelLinksChanges() {
    this.getLinks();
  }

  cancelConnectorChanges() {
    this.getSkeepersConnector();
  }

  private getLinks() {
    this.basicService.showSpinner();
    this.linkShortenerService
      .getLinks(this.siteControl.value)
      .pipe(finalize(() => this.basicService.hideSpinner()))
      .subscribe({
        next: result => this.initSubConfig(result),
        error: () => {
          this.subconfigs = [];
          this.siteControl.setValue(null, { emitEvent: false });
        },
      });
  }

  private getSkeepersConnector() {
    this.basicService.showSpinner();
    this.linkShortenerService
      .getSkeepersConnector()
      .pipe(finalize(() => this.basicService.hideSpinner()))
      .subscribe({
        next: result => this.form.patchValue(result),
        error: () => this.form.patchValue({ allowed: true }),
      });
  }

  private initSubConfig(result) {
    const linksConfigs = result.links_sent || [];
    const tagLabelPerTaskTagId = result.tag_labels_by_task_tag_id || null;

    // add default config if missing to save migration script
    ['quick-replies', 'SMS'].forEach(subvalue => this.addDefaultEntry(linksConfigs, subvalue));

    this.subconfigs = [];
    ['answers', 'publish', 'bot-answers', 'quick-replies', 'SMS'].forEach(id =>
      linksConfigs
        .filter(config => config.id === id)
        .forEach(tabConfig => {
          tabConfig.tagLabelPerTaskTagId = tagLabelPerTaskTagId;
          this.subconfigs.push(tabConfig);
        })
    );
    this.shouldDisableSaveForLinks(true);
  }

  private addDefaultEntry(linksConfigs, subentry) {
    if (!linksConfigs.find(config => config.id === subentry)) {
      linksConfigs.push({
        id: subentry,
        shortener_is_active: false,
        exceptions: [],
        transformations: [],
      });
    }
  }
}
